import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, BusinessPartner, type WorkflowFlatDto, WorkflowStatus, QuotationStep, DeliveryStep, Steps, type QuotationFlatDto, type DeliveryFlatDto, type AssigneeFlatDto, isApiException } from '@/api/eOrdersApi'
import { isAxiosError } from 'axios';
import { formatDate } from "@/utils/date.ts";
import { deserializeJson } from '@/utils/jsonDeserializer.ts';
import { showWarning } from '@/utils/utils';
import { PurchaseOrder, isVehicleOrderType } from "@/types/enums/PurchaseOrder.ts";

interface TransformedWorkflow {
  tenant?: string;
  externalReference: string;
  manufacturer: string;
  model: string;
  modelTrim: string;
  modelDescription?: Record<string, string>;
  status: WorkflowStatus;
  step: string;
  createdDate: string
  createdAtRaw?: string
  expectedDeliveryDate: string
  handoverDate?: string
  quotation?: QuotationFlatDto | undefined;
  delivery?: DeliveryFlatDto | undefined;
  assignees?: AssigneeFlatDto[] | undefined;
  supplier: BusinessPartner | undefined;
  supplierEmails: string[];
  hasMultipleSuppliers: boolean;
  clientId?: string;
  clientName?: string;
  description?: string;
  purchaseOrders?: any[];
}

interface WorkflowFilters {
  externalReference?: string[];
  clientName?: string;
  client?: string;
  status?: WorkflowStatus[];
  step?: string[];
  statusStep?: { status: WorkflowStatus; step: Steps }[];
  manufacturer?: string;
  model?: string;
  description?: string;
  supplierName?: string;
  orderNumber?: string;
  vehicle?: string;
  assignee?: string;
  dateFilter?: string;
  assigned?: boolean;
  myClient?: boolean;
  tenant?: any;
  search?: string;
}

export const useWorkflowsData = () => {
  const { locale, t } = useI18n()
  const rawData = ref<any>(null)
  const status = ref<'idle' | 'pending' | 'success' | 'error'>('idle')
  const error = ref<string | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const currentFilters = ref<WorkflowFilters>({ assigned: false, myClient: false })
  let fetchVersion = 0
  
  const getWorkflowStep = (workflow: WorkflowFlatDto): string | null => {
    if (!workflow.status) return null;
    
    if ([WorkflowStatus.OrderProcessing, WorkflowStatus.OrderUpdated, WorkflowStatus.Delivery, WorkflowStatus.DeliveryRejection, WorkflowStatus.Completed, WorkflowStatus.Archived].includes(workflow.status)) {
      const purchaseOrders = workflow.purchaseOrders;
      
      if (purchaseOrders?.length === 1 && purchaseOrders[0]?.type === PurchaseOrder.Tyre) {
        return purchaseOrders[0]?.currentProcessStep ?? null;
      }
      
      return workflow.delivery?.currentProcessStep ?? null;
    }
    
    if ([WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejection].includes(workflow.status)) {
      return workflow.quotation?.currentProcessStep ?? null;
    }
    
    if (workflow.status === WorkflowStatus.Rejected) {
      if (workflow.delivery?.currentProcessStep === DeliveryStep.Rejected) {
        return workflow.delivery.currentProcessStep;
      }
      
      const rejectedOrder = workflow.purchaseOrders?.find(po => po.currentProcessStep === 'Rejected');
      if (rejectedOrder) return rejectedOrder.currentProcessStep;
      
      if (workflow.quotation?.currentProcessStep === QuotationStep.Rejected) {
        return workflow.quotation.currentProcessStep;
      }
      
      return null;
    }
    
    if ([WorkflowStatus.OrderApproval, WorkflowStatus.OrderRejection].includes(workflow.status)) {
      const purchaseOrders = workflow.purchaseOrders;
      
      if (!purchaseOrders || purchaseOrders.length === 0) return null;
      
      const vehicleOrder = purchaseOrders.find(po => isVehicleOrderType(po.type));
      
      if (vehicleOrder) {
        return vehicleOrder.currentProcessStep ?? null;
      }
      
      return purchaseOrders[0]?.currentProcessStep ?? null;
    }
    
    return null;
  };
  
  const getWorkflowExpectedDeliveryDate = (workflow: WorkflowFlatDto): string | null => {
    const purchaseOrders = workflow.purchaseOrders;
    
    if (!purchaseOrders || purchaseOrders.length === 0) return null;
    
    const vehicleOrder = purchaseOrders.find(po => isVehicleOrderType(po.type));
    
    if (vehicleOrder) {
      return vehicleOrder.dealerExpectedDeliveryDate ?? null;
    }
    
    return purchaseOrders[0]?.dealerExpectedDeliveryDate ?? null;
  };
  
  const toPartner = (id?: string, name?: string): BusinessPartner | undefined => id ? { id, name } : undefined
  
  const getWorkflowSupplier = (workflow: WorkflowFlatDto): BusinessPartner | undefined => {
    const purchaseOrders = workflow.purchaseOrders;
    
    if (!purchaseOrders || purchaseOrders.length === 0) {
      return toPartner(workflow.quotation?.dealerId, workflow.quotation?.dealerName);
    }
    
    const vehicleOrder = purchaseOrders.find(po => isVehicleOrderType(po.type));
    
    if (vehicleOrder) {
      return toPartner(vehicleOrder.supplierId, vehicleOrder.supplierName);
    }
    
    const suppliers = purchaseOrders.map(po => toPartner(po?.supplierId, po?.supplierName)).filter(s => !!s) as BusinessPartner[];
    
    if (suppliers.length === 0) return undefined;
    
    const uniqueById = Array.from(new Map(suppliers.map(s => [s.id ?? JSON.stringify(s), s])).values());
    
    return uniqueById.length === 1 ? uniqueById[0] : undefined;
  };
  
  const uniqueEmails = (emails: (string | undefined)[]): string[] => Array.from(new Set(emails.map(e => e?.trim()).filter((e): e is string => !!e)))
  
  const getWorkflowSupplierEmails = (workflow: WorkflowFlatDto): string[] => {
    const purchaseOrders = workflow.purchaseOrders;
    
    if (!purchaseOrders || purchaseOrders.length === 0) return [];
    
    const vehicleOrder = purchaseOrders.find(po => isVehicleOrderType(po.type));
    
    if (vehicleOrder) {
      return uniqueEmails(vehicleOrder.contactEmailForPO ?? []);
    }
    
    return uniqueEmails(purchaseOrders.flatMap(po => po.contactEmailForPO ?? []));
  };
  
  const transformWorkflowData = (workflow: WorkflowFlatDto): TransformedWorkflow => {
    return <TransformedWorkflow>{
      tenant: workflow.tenant,
      externalReference: workflow.externalReference,
      manufacturer: workflow.vehicle?.manufacturer,
      model: workflow.vehicle?.model,
      modelTrim: workflow.vehicle?.trim,
      modelDescription: workflow.vehicle?.modelDescription,
      status: workflow.status,
      step: getWorkflowStep(workflow),
      createdDate: workflow.createdDate ? formatDate(workflow.createdDate, 'datetime', locale.value) : '',
      createdAtRaw: workflow.createdDate || undefined,
      expectedDeliveryDate: (() => {
        const expectedDate = getWorkflowExpectedDeliveryDate(workflow);
        return expectedDate ? formatDate(expectedDate, 'short', locale.value) : '';
      })(),
      handoverDate: workflow.delivery?.handoverDate ? formatDate(workflow.delivery.handoverDate, 'short', locale.value) : undefined,
      description: workflow.quotation?.description,
      quotation: workflow.quotation,
      delivery: workflow.delivery,
      assignees: workflow.assignees,
      supplier: getWorkflowSupplier(workflow),
      supplierEmails: getWorkflowSupplierEmails(workflow),
      hasMultipleSuppliers: (() => {
        const pos = workflow.purchaseOrders || [];
        if (!pos || pos.length <= 1) return false;
        const suppliers = pos.map(po => po?.supplierName).filter(n => !!n);
        return new Set(suppliers).size > 1;
      })(),
      clientId: workflow.clientId,
      clientName: workflow.clientName,
      purchaseOrders: workflow.purchaseOrders?.map(po => ({ ...po, supplier: toPartner(po.supplierId, po.supplierName), supplierEmails: uniqueEmails(po.contactEmailForPO ?? []) }))
    }
  }
  
  const data = computed<TransformedWorkflow[]>(() => {
    if (!rawData.value) {
      return []
    }
    
    let dataArray: WorkflowFlatDto[] = []
    
    if (rawData.value.data) {
      if (Array.isArray(rawData.value.data)) {
        dataArray = rawData.value.data
      } else if (rawData.value.data.$values && Array.isArray(rawData.value.data.$values)) {
        dataArray = rawData.value.data.$values
      }
    }
    
    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return []
    }
    
    return dataArray.map(transformWorkflowData)
  })
  
  const fetchWorkflows = async (size?: number, page?: number, filters?: WorkflowFilters, withoutStatusChange: boolean = false) => {
    const thisVersion = ++fetchVersion
    
    try {
      if (!withoutStatusChange) status.value = 'pending'
      error.value = null
      
      const requestSize = size || pageSize.value
      const requestPage = page || currentPage.value
      const requestFilters = filters || currentFilters.value
      const assigned = requestFilters.assigned === true
      const myClient = requestFilters.myClient === true
      const externalReferences = requestFilters.externalReference || [];
      const client = requestFilters.client;
      
      const processStatuses = (filters: typeof requestFilters): WorkflowStatus[] | undefined => {
        const statuses: WorkflowStatus[] = []
        
        if (filters.status && filters.status.length > 0) {
          statuses.push(...filters.status)
        }
        
        if (filters.statusStep && filters.statusStep.length > 0) {
          const statusesFromSteps = filters.statusStep.map(item => item.status as WorkflowStatus)
          statuses.push(...statusesFromSteps)
        }
        
        const uniqueStatuses = Array.from(new Set(statuses))
        return uniqueStatuses.length > 0 ? uniqueStatuses : undefined
      }
      
      const processSteps = (filters: typeof requestFilters): Steps[] | undefined => {
        const steps: Steps[] = []
        
        if (filters.statusStep && filters.statusStep.length > 0) {
          steps.push(...filters.statusStep.map(item => item.step as Steps))
        }
        
        if (filters.step && filters.step.length > 0) {
          steps.push(...filters.step as Steps[])
        }
        
        const uniqueSteps = Array.from(new Set(steps))
        return uniqueSteps.length > 0 ? uniqueSteps : undefined
      }
      
      const uniqueStatuses = processStatuses(requestFilters)
      const uniqueSteps = processSteps(requestFilters)
      const description = requestFilters.description || undefined
      const supplier = requestFilters.supplierName || undefined
      const orderNumber = requestFilters.orderNumber || undefined
      const vehicle = requestFilters.vehicle || undefined
      const search = requestFilters.search || undefined
      const assignee = requestFilters.assignee || undefined

      const result = await api.getWorkflowDtosPaginated(
        requestPage,
        requestSize,
        assigned,
        myClient,
        uniqueStatuses,
        externalReferences,
        uniqueSteps,
        client,
        description,
        supplier,
        orderNumber,
        vehicle,
        search,
        assignee
      )
      
      if (thisVersion !== fetchVersion) return
      
      const deserializedResult = deserializeJson(result);
      
      if (deserializedResult.type === 'success') {
        status.value = 'success'
        pageSize.value = requestSize
        currentPage.value = requestPage
        currentFilters.value = requestFilters
        rawData.value = deserializedResult.data
      } else {
        status.value = 'error'
        error.value = `API call failed: ${result.type}`
        
        rawData.value = {
          pageSize: requestSize,
          currentPage: requestPage,
          totalItems: 0,
          totalPages: 0,
          data: { $values: [] }
        }
      }
    } catch (err) {
      if (thisVersion !== fetchVersion) return
      
      const httpStatus = isAxiosError(err) ? (err.response?.status ?? 0) : (isApiException(err) ? err.status : 0)
      
      if (httpStatus === 502 && (filters?.assignee ?? currentFilters.value.assignee)) {
        showWarning(t('workflows.table.filters.assigneeUnavailable'))
      }
      
      status.value = 'error'
      error.value = err instanceof Error ? err.message : 'Unknown error'
      
      rawData.value = {
        pageSize: pageSize.value,
        currentPage: currentPage.value,
        totalItems: 0,
        totalPages: 0,
        data: { $values: [] }
      }
    }
  }
  
  const applyFilters = async (filters: WorkflowFilters) => {
    currentFilters.value = filters
    currentPage.value = 1
    await fetchWorkflows(pageSize.value, 1, filters, true)
  }
  
  const clearFilters = async () => {
    currentFilters.value = { assigned: false, myClient: false }
    currentFilters.value = {}
    currentPage.value = 1
    await fetchWorkflows(pageSize.value, 1, {})
  }
  
  const goToPage = async (page: number) => {
    if (page !== currentPage.value && page > 0) {
      await fetchWorkflows(pageSize.value, page, currentFilters.value)
    }
  }
  
  const changePageSize = async (newSize: number) => {
    if (newSize !== pageSize.value) {
      await fetchWorkflows(newSize, 1, currentFilters.value)
    }
  }
  
  const pagination = computed(() => {
    if (!rawData.value) return null
    
    return {
      pageSize: rawData.value.pageSize || pageSize.value,
      currentPage: (rawData.value.currentPage || 0),
      totalItems: rawData.value.totalItems || 0,
      totalPages: rawData.value.totalPages || 0
    }
  })
  
  return {
    data,
    status,
    error,
    pagination,
    currentPage: computed(() => currentPage.value),
    pageSize: computed(() => pageSize.value),
    currentFilters: computed(() => currentFilters.value),
    fetchWorkflows,
    applyFilters,
    clearFilters,
    goToPage,
    changePageSize,
    rawData: computed(() => rawData.value)
  }
}